/**
 * Qué rol satisface qué exigencia.
 *
 * Espeja lo que hace el backend en `app/dependencies.py`, que es la autoridad
 * real: el navegador sólo decide qué pantalla enseña, no qué datos se entregan.
 *
 *   require_admin       → role == 'admin'
 *   require_reclutador  → role in ('reclutador', 'admin')
 *   get_current_candidate → cualquier sesión válida
 *
 * Es decir: **admin incluye reclutador**. Las dos capas discrepaban —el backend
 * dejaba pasar al administrador y el router comparaba los roles por igualdad
 * estricta—, así que un administrador rebotaba a `/admin` y no podía entrar a
 * ninguna pantalla del reclutador. En una instancia recién arrancada, donde la
 * primera persona es admin y titular y nadie puede cambiarse el rol a sí mismo,
 * eso dejaba el producto sin usar hasta que entrara una segunda persona.
 */

export type Rol = 'candidato' | 'reclutador' | 'admin';

/** Roles que satisfacen cada exigencia, del más específico al más amplio. */
const SATISFACEN: Record<Rol, readonly string[]> = {
  admin: ['admin'],
  reclutador: ['reclutador', 'admin'],
  // Las rutas de candidato son el flujo de postulación. El backend no exige rol
  // para ellas, sólo sesión: quien recluta o administra también puede postularse.
  candidato: ['candidato', 'reclutador', 'admin'],
};

export function satisfaceRol(rolUsuario: string, rolExigido: Rol): boolean {
  return SATISFACEN[rolExigido].includes(rolUsuario.toLowerCase().trim());
}

/** A dónde mandar a quien no puede estar donde está. */
export function rutaPorDefecto(rolUsuario: string): string {
  switch (rolUsuario.toLowerCase().trim()) {
    case 'admin':
      return '/admin';
    case 'reclutador':
      return '/recruiter';
    // Una cuenta 'pendiente' pertenece a la organización pero aún no tiene rol
    // asignado. No es un candidato: mandarla a /home la metería en el flujo de
    // postulación, que no es lo suyo.
    case 'pendiente':
      return '/pendiente';
    default:
      return '/home/dashboard';
  }
}
